import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  SectionCard,
  TextInput,
} from "@a2z/ui";
import type { FeatureFlagView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { FeatureFlagRow } from "../components/FeatureFlagRow";
import { FeatureFlagFormDialog } from "../components/FeatureFlagFormDialog";
import { useFeatureFlagsQuery } from "../services/useFeatureFlagsQuery";

type Props = {
  locale: string;
};

// Dialog state: closed, creating, or editing a specific flag.
type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; flag: FeatureFlagView };

export function FeatureFlagsPage({ locale }: Props) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin =
    session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);

  const { data: flags, isLoading, error, refetch } = useFeatureFlagsQuery();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (flags ?? []).filter((flag) => {
      if (!q) return true;
      const name = (locale === "ar" ? flag.nameAr : flag.nameEn) ?? "";
      return (
        flag.key.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q)
      );
    });
  }, [flags, query, locale]);

  if (isSessionLoading || (!isSuperAdmin && !isSessionLoading) || isLoading) {
    return <LoadingSkeleton variant="list" rows={6} />;
  }
  if (error || !flags) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <div className="flex w-full flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.featureFlags.title)}
          </h1>
          <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
            {t(keys.featureFlags.subtitle)}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setDialog({ mode: "create" })}
          className="h-10 rounded-lg px-4 text-sm font-semibold"
        >
          {t(keys.featureFlags.addAction)}
        </Button>
      </div>

      {flags.length > 0 ? (
        <div className="w-full sm:max-w-xs">
          <TextInput
            name="flag-search"
            type="search"
            value={query}
            placeholder={t(keys.featureFlags.searchPlaceholder)}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      ) : null}

      {flags.length === 0 ? (
        <EmptyState
          title={t(keys.featureFlags.emptyTitle)}
          description={t(keys.featureFlags.emptySubtitle)}
        />
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-[color:var(--text-muted)]">
          {t(keys.featureFlags.noResults)}
        </p>
      ) : (
        <SectionCard
          title={t(keys.featureFlags.title)}
          right={
            <span className="text-xs font-semibold text-[color:var(--text-muted)]">
              {filtered.length}
            </span>
          }
        >
          <ul className="flex flex-col gap-2">
            {filtered.map((flag) => (
              <FeatureFlagRow
                key={flag.id}
                flag={flag}
                onEdit={(f) => setDialog({ mode: "edit", flag: f })}
              />
            ))}
          </ul>
        </SectionCard>
      )}

      {dialog.mode !== "closed" ? (
        <FeatureFlagFormDialog
          // Remount on target change so the draft re-seeds from the flag.
          key={dialog.mode === "edit" ? dialog.flag.id : "create"}
          isOpen
          flag={dialog.mode === "edit" ? dialog.flag : undefined}
          onClose={() => setDialog({ mode: "closed" })}
        />
      ) : null}
    </div>
  );
}
